import CanvasPluginProvider from '@/components/canvas/CanvasPluginProvider'
import { useMeasurementStore } from '@/store/measurements'
import Konva from 'konva'
import { useCamerasStore } from '@/store/cameras'
import { CanvasPluginSettings } from '@/types/Canvas'
import { Reactive } from 'vue'

type HumanPosition = {
  time: string
  height: number
  score: number
  width: number
  xCenter: number
  yCenter: number
  camera: string
  label: string
}

type TrackPoint = {
  x: number
  y: number
  time: number
  score: number
  w: number
  h: number
  camera: string
}

type Track = {
  points: TrackPoint[]
}

type ConfigType = {
  enabled: boolean
  durationMs: number
  minScore: number
}

const TRACK_COLORS = ['#4F8EF7', '#F76F4F', '#4FC77A', '#F7C94F', '#C44FF7', '#4FF0F7', '#F74FA3']
const STITCH_GAP_MS = 1000 * 60 * 30
const ARROW_SPACING_PX = 80
const DASH_SPEED = 3

function buildArrowhead(x: number, y: number, angle: number, color: string, size: number): Konva.Line {
  return new Konva.Line({
    points: [
      x - Math.cos(angle - Math.PI / 6) * size,
      y - Math.sin(angle - Math.PI / 6) * size,
      x,
      y,
      x - Math.cos(angle + Math.PI / 6) * size,
      y - Math.sin(angle + Math.PI / 6) * size,
    ],
    stroke: color,
    strokeWidth: 2.5,
    opacity: 1,
    lineCap: 'round',
    lineJoin: 'round',
    strokeScaleEnabled: false,
    listening: false,
  })
}

function addArrowsAlongPath(group: Konva.Group, points: TrackPoint[], color: string, spacingPx: number, arrowSize: number) {
  let distanceSinceLastArrow = spacingPx / 2

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]
    const curr = points[i]
    const dx = curr.x - prev.x
    const dy = curr.y - prev.y
    const segLen = Math.sqrt(dx * dx + dy * dy)
    if (segLen === 0) continue

    const angle = Math.atan2(dy, dx)
    let walked = 0

    while (distanceSinceLastArrow + (segLen - walked) >= spacingPx) {
      const remaining = spacingPx - distanceSinceLastArrow
      walked += remaining
      const t = walked / segLen
      group.add(buildArrowhead(prev.x + dx * t, prev.y + dy * t, angle, color, arrowSize))
      distanceSinceLastArrow = 0
    }

    distanceSinceLastArrow += segLen - walked
  }
}

export default class HumanTrackingPlugin extends CanvasPluginProvider<ConfigType> {
  reactiveProperties: Reactive<{ enabled: boolean; loading: boolean; positions: HumanPosition[] }>
  private animation: Konva.Animation | null = null

  constructor(settings: CanvasPluginSettings, config: ConfigType) {
    super(settings, 'humanTracking', config, true, true)
    this.reactiveProperties = { loading: false, enabled: settings.enabled, positions: [] }
  }

  fetchInformation(): [Promise<void>, AbortController] {
    this.reactiveProperties.positions = []
    this.draw()
    const controller = new AbortController()

    const endMs = this.settings.timestamp.toMillis()
    const startMs = endMs - this.config.durationMs

    const promise = new Promise<void>((resolve, reject) => {
      useMeasurementStore()
        .loadGenericMeasurement(
          'longPosition',
          {
            groupByTags: true,
            time: { start: startMs, end: endMs },
            where: { label: ['human'] },
          },
          controller.signal
        )
        .then(result => {
          this.reactiveProperties.positions = result.filter((position: { score: number }) => position.score >= this.config.minScore)
          resolve()
        })
        .catch(reject)
    })

    return [promise, controller]
  }

  private stopAnimation() {
    if (this.animation) {
      this.animation.stop()
      this.animation = null
    }
  }

  private startAnimation(chaseLines: Konva.Line[]) {
    this.stopAnimation()
    this.animation = new Konva.Animation(() => {
      for (const line of chaseLines) {
        line.dashOffset((line.dashOffset() ?? 0) - DASH_SPEED)
      }
    }, this.group.getLayer())
    this.animation.start()
  }

  private buildTracks(): Track[] {
    const allPoints: TrackPoint[] = []

    for (const position of this.reactiveProperties.positions as HumanPosition[]) {
      const cameraId = position.camera
      const elementPosition = this.settings.elementPositions[cameraId]
      if (!elementPosition) continue

      const camera = useCamerasStore().getCameraById(undefined, cameraId)
      if (!camera) continue

      const x = (position.xCenter - position.width / 2) * elementPosition.width
      const y = (position.yCenter - position.height / 2) * elementPosition.height
      const w = position.width * elementPosition.width
      const h = position.height * elementPosition.height

      const transform = new Konva.Transform()
      transform.translate(elementPosition.x, elementPosition.y)
      transform.rotate(elementPosition.rotation * (Math.PI / 180))

      const canvasPos = transform.point({ x: x + w / 2, y: y + h / 2 })

      allPoints.push({
        x: canvasPos.x,
        y: canvasPos.y,
        time: new Date(position.time).getTime(),
        score: position.score,
        w,
        h,
        camera: cameraId,
      })
    }

    allPoints.sort((a, b) => a.time - b.time)

    const tracks: Track[] = []
    let current: Track | null = null

    for (const point of allPoints) {
      if (!current) {
        current = { points: [point] }
        tracks.push(current)
        continue
      }

      const last = current.points[current.points.length - 1]
      const gap = point.time - last.time

      if (gap > STITCH_GAP_MS) {
        current = { points: [point] }
        tracks.push(current)
      } else {
        current.points.push(point)
      }
    }

    return tracks
  }

  draw() {
    this.stopAnimation()
    this.group.destroyChildren()
    if (!this.reactiveProperties.positions?.length) return

    const tracks = this.buildTracks()
    const trackGroups: Konva.Group[] = []
    const chaseLinesByTrack: Konva.Line[][] = []

    const hoverCountByTrack: number[] = tracks.map(() => 0)
    let hoveredTrackIdx: number | null = null

    const onHoverStart = (trackIdx: number) => {
      hoverCountByTrack[trackIdx]++
      if (hoveredTrackIdx === trackIdx) return
      hoveredTrackIdx = trackIdx
      trackGroups.forEach((tg, i) => tg.opacity(i === trackIdx ? 1 : 0.1))
      chaseLinesByTrack[trackIdx].forEach(l => l.opacity(1))
      this.startAnimation(chaseLinesByTrack[trackIdx])
    }

    const onHoverEnd = (trackIdx: number) => {
      hoverCountByTrack[trackIdx]--
      setTimeout(() => {
        if (hoverCountByTrack[trackIdx] > 0) return
        if (hoveredTrackIdx !== trackIdx) return
        hoveredTrackIdx = null
        chaseLinesByTrack[trackIdx].forEach(l => l.opacity(0))
        this.stopAnimation()
        trackGroups.forEach(tg => tg.opacity(1))
      }, 50)
    }

    for (let trackIdx = 0; trackIdx < tracks.length; trackIdx++) {
      const track = tracks[trackIdx]
      const color = TRACK_COLORS[trackIdx % TRACK_COLORS.length]
      const points = track.points

      const g = new Konva.Group()
      this.group.add(g)
      trackGroups.push(g)
      chaseLinesByTrack.push([])

      if (points.length === 0) continue

      const dotRadius = Math.max(points[Math.floor(points.length / 2)].w, points[Math.floor(points.length / 2)].h) / 4

      if (points.length >= 2) {
        const flatPoints = points.flatMap(p => [p.x, p.y])

        g.add(
          new Konva.Line({
            points: flatPoints,
            stroke: color,
            strokeWidth: 4,
            opacity: 0.35,
            lineCap: 'round',
            lineJoin: 'round',
            strokeScaleEnabled: false,
            listening: false,
          })
        )

        const chaseLine = new Konva.Line({
          points: flatPoints,
          stroke: color,
          strokeWidth: 4,
          opacity: 0,
          lineCap: 'round',
          lineJoin: 'round',
          dash: [24, 32],
          dashOffset: 0,
          strokeScaleEnabled: false,
          listening: false,
        })
        g.add(chaseLine)
        chaseLinesByTrack[trackIdx].push(chaseLine)

        addArrowsAlongPath(g, points, color, ARROW_SPACING_PX, dotRadius * 1.2)
      }

      points.forEach((point, idx) => {
        const isHead = idx === points.length - 1
        const isTail = idx === 0

        if (isHead) {
          g.add(
            new Konva.Circle({
              x: point.x,
              y: point.y,
              radius: dotRadius * 2,
              stroke: color,
              strokeWidth: 2,
              fill: 'transparent',
              opacity: 1,
              strokeScaleEnabled: false,
              listening: false,
            })
          )
        }

        g.add(
          new Konva.Circle({
            x: point.x,
            y: point.y,
            radius: dotRadius,
            fill: color,
            opacity: isTail || isHead ? 1 : 0.6,
            strokeScaleEnabled: false,
            listening: false,
          })
        )

        const time = new Date(point.time).toLocaleTimeString()
        const text = `Position ${idx + 1} / ${points.length} — ${time} — score ${(point.score * 100).toFixed(0)}%`
        const hitCircle = new Konva.Circle({
          x: point.x,
          y: point.y,
          radius: dotRadius * 2,
          fill: 'transparent',
          strokeScaleEnabled: false,
        })

        hitCircle.on('mouseenter', ev => {
          this.settings.tooltipEnable({ x: ev.evt.clientX + 2, y: ev.evt.clientY + 2, text })
          onHoverStart(trackIdx)
        })
        hitCircle.on('mousemove', ev => {
          this.settings.tooltipEnable({ x: ev.evt.clientX + 2, y: ev.evt.clientY + 2, text })
        })
        hitCircle.on('mouseleave', () => {
          this.settings.tooltipDisable()
          onHoverEnd(trackIdx)
        })

        g.add(hitCircle)
      })
    }
  }

  doesConfigChangeNeedRerender(): boolean {
    return true
  }

  doesConfigChangeNeedReload(config: ConfigType): boolean {
    return config.durationMs !== this.config.durationMs
  }
}
